/**
 * Ad eligibility — provider-agnostic. Ads are shown to GUEST + FREE users
 * only. Admins, VIPs, and every paid subscriber tier NEVER see ads, and ads
 * only render on a deployed stage (where `ADS_ENABLED` is set). A guest is
 * unauthenticated (`user === null`) and always free-tier. This is the single
 * gate both consoles use to decide whether to render an `<AdSlot>`.
 *
 * This policy is business rule, not vendor wiring: it survived the AdSense
 * removal unchanged and applies to whatever network is wired in next.
 */
import type { AuthUser } from '../auth/AuthContext.ts';
import { ADS_ENABLED } from '../config/features.ts';

/** Groups that are NEVER shown ads: admins + every paid subscription tier +
 *  `test` (ephemeral e2e/automation accounts — keeps ad units out of test
 *  runs so they can't perturb console assertions). */
const AD_FREE_GROUPS: ReadonlySet<string> = new Set([
  'admin',
  'vip',
  'pro',
  'plus',
  'premium',
  'test',
]);

/** True when the viewer should see ads — guest or free only, on a deployed
 *  stage. Admin / VIP / paid are always ad-free. */
export function shouldShowAds(user: AuthUser | null): boolean {
  const groups = user?.groups ?? [];
  return ADS_ENABLED && !groups.some((g) => AD_FREE_GROUPS.has(g));
}
